import { useState } from 'react';
import { Link } from 'react-router';
import { AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { hub } from '@/lib/hub-client';

/**
 * Same sentence whether or not the address is known — the hub answers 204
 * either way so nothing here can leak which emails exist.
 */
export const FORGOT_SENT_MESSAGE =
  'If an account exists for that address, we sent a reset link. It expires in one hour.';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await hub.authPasswordResetRequest({ body: { email } });
      setSent(true);
    } catch (err) {
      setError((err as Error).message ?? 'Could not send the reset link');
      setPending(false);
    }
  };

  const backToSignIn = (
    <p className="text-center text-sm">
      <Link to="/login" className="underline underline-offset-4">
        Back to sign in
      </Link>
    </p>
  );

  if (sent) {
    return (
      <AuthShell title="Check your email" subtitle="Trackframe">
        <p className="text-sm text-muted-foreground">{FORGOT_SENT_MESSAGE}</p>
        {backToSignIn}
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password" subtitle="Trackframe">
      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" className="w-full" disabled={pending}>
          {pending ? 'Sending…' : 'Send reset link'}
        </Button>
      </form>
      {backToSignIn}
    </AuthShell>
  );
}
